import type { OwnerCell } from "../../lib/connections/owners";
import { cx } from "./parts";

/** An eye: what is shown. */
function EyeGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

/** Above the list while Other members is chosen, so nobody mistakes whose accounts they are looking at. */
export function MembersNote({ count }: { count: number }) {
  const one = count === 1;
  return (
    <p className="flex items-start gap-2.5 rounded-xl border border-border bg-secondary px-4 py-2.5 text-[13px]/5 text-body-foreground">
      <span className="mt-0.5 shrink-0 text-muted-foreground">
        <EyeGlyph />
      </span>
      <span>
        {one
          ? "Showing 1 personal account that belongs to another member. Only owners and admins can see it, and only the person it belongs to can change it."
          : `Showing ${count} personal accounts that belong to other members. Only owners and admins can see them, and only the person they belong to can change them.`}
      </span>
    </p>
  );
}

/** On someone else's account, opened through Other members: whose it is, and why nothing changes it. */
export function OwnerNotice({
  owner,
  className,
}: {
  owner: OwnerCell;
  className?: string | undefined;
}) {
  return (
    <p
      className={cx(
        "flex items-start gap-2.5 rounded-xl border border-border bg-secondary px-4 py-3 text-[13px]/5 text-body-foreground",
        className,
      )}
    >
      <span className="mt-0.5 shrink-0 text-muted-foreground">
        <EyeGlyph />
      </span>
      <span>{noticeWords(owner)}</span>
    </p>
  );
}

/** A former member has no one left who can change the account, so it promises nothing. */
function noticeWords(owner: OwnerCell): string {
  switch (owner.kind) {
    case "former":
      return "This personal account belongs to a former member.";
    case "unknown":
      return "This is another member's personal account. Its owner is unavailable right now. Only they can change it.";
    default:
      return `This is ${owner.label}'s personal account. Only they can change it.`;
  }
}
